import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { fetchAdmins, createAdmin, updateAdmin, deleteAdmin } from "../api/adminApi";

const AdminUsersPage: React.FC = () => {
  const [admins, setAdmins] = useState<any[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ id: "", name: "", email: "", password: "" });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(""); // Message for Snackbar
  const [editMode, setEditMode] = useState(false);

  // Fetch admin users
  useEffect(() => {
    const loadAdmins = async () => {
      const data = await fetchAdmins();
      setAdmins(data);
    };

    loadAdmins();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleCreateAdmin = async () => {
    try {
      await createAdmin(form);
      setSnackbarMessage("Admin created successfully!");
      setSnackbarOpen(true);
      setDialogOpen(false);
      setForm({ id: "", name: "", email: "", password: "" });
      const data = await fetchAdmins();
      setAdmins(data);
    } catch (error) {
      console.error("Failed to create admin:", error);
    }
  };

  const handleEditAdmin = async () => {
    try {
      await updateAdmin(form.id, form);
      setSnackbarMessage("Admin updated successfully!");
      setSnackbarOpen(true);
      setDialogOpen(false);
      setForm({ id: "", name: "", email: "", password: "" });
      setEditMode(false);
      const data = await fetchAdmins();
      setAdmins(data);
    } catch (error) {
      console.error("Failed to update admin:", error);
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (window.confirm("Are you sure you want to delete this admin?")) {
      try {
        await deleteAdmin(adminId);
        setSnackbarMessage("Admin deleted successfully!");
        setSnackbarOpen(true);
        const data = await fetchAdmins();
        setAdmins(data);
      } catch (error) {
        console.error("Failed to delete admin:", error);
      }
    }
  };

  const openEditDialog = (admin: any) => {
    setForm({ id: admin.id, name: admin.name, email: admin.email, password: "" }); // Password remains blank
    setEditMode(true);
    setDialogOpen(true);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Admin Users
      </Typography>

      <Button
        variant="contained"
        onClick={() => {
          setForm({ id: "", name: "", email: "", password: "" });
          setEditMode(false);
          setDialogOpen(true);
        }}
      >
        Add New Admin
      </Button>

      <Grid container spacing={2} sx={{ marginTop: 2 }}>
        {admins.map((admin) => (
          <Grid size={{xs: 12, sm: 6, md: 4}} key={admin.id}>
            <Card>
              <CardContent>
                <Typography variant="h6">{admin.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {admin.email}
                </Typography>
                <Box sx={{ marginTop: 1 }}>
                  <Button
                    variant="outlined"
                    sx={{ marginRight: 1 }}
                    onClick={() => openEditDialog(admin)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleDeleteAdmin(admin.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Admin Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editMode ? "Edit Admin" : "Create New Admin"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={form.name}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Email"
            name="email"
            value={form.email}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            value={form.password}
            onChange={handleInputChange}
            fullWidth
            margin="dense"
            helperText={editMode ? "Leave blank to keep the current password" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={editMode ? handleEditAdmin : handleCreateAdmin}
            variant="contained"
          >
            {editMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsersPage;
